import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Box,
  Button,
  TextField,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { useTranslation } from '../i18n/useTranslation';

const SettingsPage: React.FC = () => {
  const auth = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation();

  const cardStyle = {
    marginBottom: 24,
    maxWidth: 500,
    width: '100%'
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, fontWeight: 800, color: '#1e293b', marginBottom: 8 }}>{t('settings.title')}</h1>
        <p style={{ color: '#64748b', fontSize: 16 }}>{t('settings.subtitle')}</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* User Profile Settings */}
        <Card elevation={2} sx={cardStyle}>
          <CardHeader 
            title={t('settings.userProfile')} 
            sx={{ 
              bgcolor: '#1565c0',
              color: 'white',
              '& .MuiCardHeader-title': { fontWeight: 600 }
            }}
          />
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label={t('settings.email')}
                value={auth.user?.email || 'admin@finscore.ai'}
                disabled
                fullWidth
                size="small"
              />
              <TextField
                label={t('settings.role')}
                value={auth.user?.role?.toUpperCase() || 'ADMIN'}
                disabled
                fullWidth
                size="small"
              />
              {auth.user?.merchantId && (
                <TextField
                  label={t('settings.merchantId')}
                  value={auth.user.merchantId}
                  disabled
                  fullWidth
                  size="small"
                />
              )}
              <Button 
                variant="contained" 
                sx={{ 
                  bgcolor: '#1565c0',
                  '&:hover': { bgcolor: '#0d47a1' },
                  mt: 1
                }}
              >
                {t('settings.updateProfile')}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* System Preferences */}
        <Card elevation={2} sx={cardStyle}>
          <CardHeader 
            title={t('settings.systemPreferences')} 
            sx={{ 
              bgcolor: '#1976d2',
              color: 'white',
              '& .MuiCardHeader-title': { fontWeight: 600 }
            }}
          />
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label={t('settings.emailNotifications')}
              />
              <FormControlLabel
                control={<Switch defaultChecked />}
                label={t('settings.smsAlerts')}
              />
              <FormControlLabel
                control={<Switch />}
                label={t('settings.weeklyReports')}
              />
              <FormControlLabel
                control={<Switch defaultChecked />}
                label={t('settings.riskAlerts')}
              />
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {t('settings.languageInfo')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('settings.timezoneInfo')}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Security Settings */}
        <Card elevation={2} sx={cardStyle}>
          <CardHeader 
            title={t('settings.security')} 
            sx={{ 
              bgcolor: '#1976d2',
              color: 'white',
              '& .MuiCardHeader-title': { fontWeight: 600 }
            }}
          />
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button 
                variant="outlined" 
                sx={{ 
                  borderColor: '#1565c0',
                  color: '#1565c0',
                  '&:hover': { borderColor: '#0d47a1', bgcolor: 'rgba(21, 101, 192, 0.04)' }
                }}
              >
                {t('settings.changePassword')}
              </Button>
              <Button 
                variant="outlined" 
                sx={{ 
                  borderColor: '#1565c0',
                  color: '#1565c0',
                  '&:hover': { borderColor: '#0d47a1', bgcolor: 'rgba(21, 101, 192, 0.04)' }
                }}
              >
                {t('settings.enable2FA')}
              </Button>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label={t('settings.sessionTimeout')}
              />
              <Typography variant="body2" color="text.secondary">
                {t('settings.lastLogin')}: {new Date().toLocaleDateString()} à {new Date().toLocaleTimeString()}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Platform Info */}
        <Card elevation={2} sx={cardStyle}>
          <CardHeader 
            title={t('settings.bridgeBankGroup')} 
            sx={{ 
              bgcolor: '#1565c0',
              color: 'white',
              '& .MuiCardHeader-title': { fontWeight: 600 }
            }}
          />
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2">
                <strong>{t('settings.institution')}:</strong> {t('settings.bridgeBankGroup')}
              </Typography>
              <Typography variant="body2">
                <strong>{t('settings.system')}:</strong> {t('settings.creditScoringEngine')}
              </Typography>
              <Typography variant="body2">
                <strong>{t('settings.currency')}:</strong> {t('settings.westAfricanCFA')}
              </Typography>
              <Typography variant="body2">
                <strong>{t('settings.region')}:</strong> {t('settings.westAfrica')}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Button 
                variant="contained" 
                sx={{ 
                  bgcolor: '#212121',
                  '&:hover': { bgcolor: '#424242' }
                }}
              >
                {t('settings.contactSupport')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SettingsPage;
